import { CHECKBOX_CONSTANTS } from './checkbox-constants';
import { getCorrectEventName } from '@material/animation';
var CheckboxFoundation = /** @class */ (function () {
    function CheckboxFoundation(_adapter) {
        this._adapter = _adapter;
        this._dense = false;
        this._currentCheckState = CHECKBOX_CONSTANTS.strings.TRANSITION_STATE_INIT;
        this._currentAnimationClass = '';
        this._animEndLatchTimer = 0;
        this._enableAnimationEndHandler = false;
    }
    CheckboxFoundation.prototype.disconnect = function () {
        this._adapter.unlisten('change', this._handleChange);
        this._adapter.disconnect();
        this._adapter.uninstallPropertyChangeHooks();
        this._adapter.unlisten(getCorrectEventName(window, 'animationend'), this._handleAnimationEnd);
    };
    CheckboxFoundation.prototype.connect = function () {
        var _this = this;
        this._adapter.initialize();
        this._currentCheckState = this._determineCheckState();
        this._setDense(this.dense);
        this._updateAriaChecked();
        this._inputAttributeChangedListener = function (name, value) { return _this._handleInputAttributeChange(); };
        this._adapter.setInputAttributeObserver(function (name, value) { return _this._inputAttributeChangedListener(name, value); });
        this._handleChange = function () { return _this._handleInputChange(); };
        this._adapter.listen('change', this._handleChange);
        this._adapter.installPropertyChangeHooks(function () {
            _this._handleInputChange();
        });
        this._handleAnimationEnd = function () { return _this.handleAnimationEnd(); };
        this._adapter.listen(getCorrectEventName(window, 'animationend'), this._handleAnimationEnd, true);
        this._handleInputChange();
    };
    Object.defineProperty(CheckboxFoundation.prototype, "dense", {
        get: function () {
            return this._dense;
        },
        set: function (value) {
            if (this._dense !== value) {
                this._dense = value;
                this._setDense(value);
            }
        },
        enumerable: true,
        configurable: true
    });
    CheckboxFoundation.prototype.handleAnimationEnd = function () {
        var _this = this;
        if (!this._enableAnimationEndHandler) {
            return;
        }
        var handler = function () {
            _this._adapter.removeRootClass(_this._currentAnimationClass);
            _this._enableAnimationEndHandler = false;
        };
        clearTimeout(this._animEndLatchTimer);
        this._animEndLatchTimer = setTimeout(handler, CHECKBOX_CONSTANTS.numbers.ANIM_END_LATCH_MS);
    };
    CheckboxFoundation.prototype._setDense = function (value) {
        this._adapter.setDense(value);
    };
    CheckboxFoundation.prototype._determineCheckState = function () {
        var _a = CHECKBOX_CONSTANTS.strings, TRANSITION_STATE_INDETERMINATE = _a.TRANSITION_STATE_INDETERMINATE, TRANSITION_STATE_CHECKED = _a.TRANSITION_STATE_CHECKED, TRANSITION_STATE_UNCHECKED = _a.TRANSITION_STATE_UNCHECKED;
        if (this._adapter.isIndeterminate()) {
            return TRANSITION_STATE_INDETERMINATE;
        }
        return this._adapter.isChecked() ? TRANSITION_STATE_CHECKED : TRANSITION_STATE_UNCHECKED;
    };
    CheckboxFoundation.prototype._updateAriaChecked = function () {
        var _a = CHECKBOX_CONSTANTS.strings, ARIA_CHECKED_INDETERMINATE_VALUE = _a.ARIA_CHECKED_INDETERMINATE_VALUE, ARIA_CHECKED_ATTR = _a.ARIA_CHECKED_ATTR;
        // Ensure aria-checked is set to mixed if checkbox is in indeterminate state.
        if (this._adapter.isIndeterminate()) {
            this._adapter.setNativeAttribute(ARIA_CHECKED_ATTR, ARIA_CHECKED_INDETERMINATE_VALUE);
            this._adapter.setRootClass(CHECKBOX_CONSTANTS.classes.INDETERMINATE);
            if (!this._adapter.isChecked()) {
                this._adapter.removeRootClass(CHECKBOX_CONSTANTS.classes.CHECKED);
            }
        }
        else {
            // The on/off state does not need to keep track of aria-checked, since
            // the screenreader uses the checked property on the checkbox element.
            this._adapter.removeNativeAttribute(ARIA_CHECKED_ATTR);
            this._adapter.removeRootClass(CHECKBOX_CONSTANTS.classes.INDETERMINATE);
        }
    };
    CheckboxFoundation.prototype._handleInputChange = function () {
        this._handleInputAttributeChange();
        this._updateCheckboxStateClasses();
        this._transitionCheckState();
    };
    CheckboxFoundation.prototype._transitionCheckState = function () {
        if (!this._adapter.isAttachedToDOM()) {
            return;
        }
        var oldState = this._currentCheckState;
        var newState = this._determineCheckState();
        if (oldState === newState) {
            return;
        }
        this._updateAriaChecked();
        var TRANSITION_STATE_UNCHECKED = CHECKBOX_CONSTANTS.strings.TRANSITION_STATE_UNCHECKED;
        var SELECTED = CHECKBOX_CONSTANTS.classes.SELECTED;
        if (newState === TRANSITION_STATE_UNCHECKED) {
            this._adapter.removeRootClass(SELECTED);
        }
        else {
            this._adapter.setRootClass(SELECTED);
        }
        // Check to ensure that there isn't a previously existing animation class, in case for example
        // the user interacted with the checkbox before the animation was finished.
        if (this._currentAnimationClass.length > 0) {
            clearTimeout(this._animEndLatchTimer);
            this._adapter.forceLayout();
            this._adapter.removeRootClass(this._currentAnimationClass);
        }
        // Check to ensure that there isn't a previously existing animation class, in case for example
        // the user interacted with the checkbox before the animation was finished.
        if (this._currentAnimationClass.length > 0) {
            clearTimeout(this._animEndLatchTimer);
            this._adapter.forceLayout();
            this._adapter.removeRootClass(this._currentAnimationClass);
        }
        this._currentAnimationClass = this._getTransitionAnimationClass(oldState, newState);
        this._currentCheckState = newState;
        // Check for parentNode so that animations are only run when the element is attached
        // to the DOM.
        if (this._adapter.isAttachedToDOM() && this._currentAnimationClass.length > 0) {
            this._adapter.setRootClass(this._currentAnimationClass);
            this._enableAnimationEndHandler = true;
        }
        this._adapter.forceLayout();
    };
    CheckboxFoundation.prototype._handleInputAttributeChange = function () {
        if (this._adapter.isDisabled()) {
            this._adapter.setRootClass(CHECKBOX_CONSTANTS.classes.DISABLED);
        }
        else {
            this._adapter.removeRootClass(CHECKBOX_CONSTANTS.classes.DISABLED);
        }
        this._updateCheckboxStateClasses();
    };
    CheckboxFoundation.prototype._getTransitionAnimationClass = function (oldState, newState) {
        var _a = CHECKBOX_CONSTANTS.strings, TRANSITION_STATE_CHECKED = _a.TRANSITION_STATE_CHECKED, TRANSITION_STATE_UNCHECKED = _a.TRANSITION_STATE_UNCHECKED;
        var _b = CHECKBOX_CONSTANTS.classes, ANIM_UNCHECKED_CHECKED = _b.ANIM_UNCHECKED_CHECKED, ANIM_UNCHECKED_INDETERMINATE = _b.ANIM_UNCHECKED_INDETERMINATE, ANIM_CHECKED_UNCHECKED = _b.ANIM_CHECKED_UNCHECKED, ANIM_CHECKED_INDETERMINATE = _b.ANIM_CHECKED_INDETERMINATE, ANIM_INDETERMINATE_CHECKED = _b.ANIM_INDETERMINATE_CHECKED, ANIM_INDETERMINATE_UNCHECKED = _b.ANIM_INDETERMINATE_UNCHECKED;
        switch (oldState) {
            case TRANSITION_STATE_UNCHECKED:
                return newState === TRANSITION_STATE_CHECKED ? ANIM_UNCHECKED_CHECKED : ANIM_UNCHECKED_INDETERMINATE;
            case TRANSITION_STATE_CHECKED:
                return newState === TRANSITION_STATE_UNCHECKED ? ANIM_CHECKED_UNCHECKED : ANIM_CHECKED_INDETERMINATE;
            default:
                // TRANSITION_STATE_INDETERMINATE
                // Handles TRANSITION_STATE_INIT
                return newState === TRANSITION_STATE_CHECKED ? ANIM_INDETERMINATE_CHECKED : ANIM_INDETERMINATE_UNCHECKED;
        }
    };
    CheckboxFoundation.prototype._updateCheckboxStateClasses = function () {
        // Since we can't use the ::slotted():checked ~ selector, simulating the same functionality with classes
        if (this._adapter.isChecked()) {
            this._adapter.setRootClass(CHECKBOX_CONSTANTS.classes.CHECKED);
        }
        else {
            this._adapter.removeRootClass(CHECKBOX_CONSTANTS.classes.CHECKED);
        }
        if (this._adapter.isIndeterminate()) {
            this._adapter.setRootClass(CHECKBOX_CONSTANTS.classes.INDETERMINATE);
            this._adapter.removeRootClass(CHECKBOX_CONSTANTS.classes.CHECKED);
        }
        else {
            this._adapter.removeRootClass(CHECKBOX_CONSTANTS.classes.INDETERMINATE);
        }
    };
    return CheckboxFoundation;
}());
export { CheckboxFoundation };
